<template>
  <div class="w-100" :class="{ 'st-has-error': hasFormatError }">
    <div class="d-flex w-100">
      <DatePicker
        ref="picker"
        v-model="innerDate"
        :placeholder="displayPlaceholder"
        :format="displayFormat"
        value-type="date"
        :disabled-date="disabledDate"
        :disabled="disabled"
        :editable="!readonly"
        :input-attr="{ name }"
        :style="style"
        class="st-datepicker st-form-picker w-100"
        clearable
        :lang="datePickerLang"
        :append-to-body="false"
        popup-class="st-datepicker-popup-right"
        @change="onChange"
        @clear="onClear"
        @input-error="onInputError"
      />
    </div>

    <div v-if="hasFormatError" class="st-datepicker-error">
      {{ $t("GENERAL.DATEPICKER.INVALID_DATE") }}
    </div>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import ro from "vue2-datepicker/locale/ro";
import en from "vue2-datepicker/locale/en";

import { createTextMaskInputElement } from "text-mask-core/dist/textMaskCore";

import i18nService from "@/shared/services/i18n.service";
import {
  buildDisplayFormat,
  buildDisplayFormatForPlaceholder,
  externalToDate,
  dateToExternal,
  buildTextMask,
  isMaskedValueComplete,
  isMaskedValueEmpty,
  isValidMaskedDateString,
} from "@/shared/utils/datePicker.helper";

export default {
  name: "StDatepicker",
  components: { DatePicker },

  data() {
    return {
      locale: i18nService.getActiveLanguage(),
      style: {},
      maskController: null,
      maskInputEl: null,
      maskInputHandler: null,
      maskBlurHandler: null,
      hasFormatError: false
    };
  },

  props: {
    value: { type: [String, null], default: null },

    placeholder: {
      type: String,
      default: null,
    },

    formatOptions: {
      type: Object,
      default: () => ({
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        separator: ".",
      }),
    },

    name: { type: String, required: true },
    min: Date,
    max: Date,
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    size: { type: String, default: "md" },
    fv: Object,
    field: { type: null },
  },

  computed: {
    displayFormat() {
      return buildDisplayFormat(this.formatOptions);
    },

    displayPlaceholder() {
      if (typeof this.placeholder === "string" && this.placeholder.trim()) {
        return this.placeholder;
      }

      return buildDisplayFormatForPlaceholder(this.formatOptions);
    },
    datePickerLang() {
      const l = (this.locale || "").toLowerCase();
      return l.startsWith("ro") ? ro : en;
    },

    innerDate: {
      get() {
        return externalToDate(this.value);
      },
      set(date) {
        if (date === null) return;

        const isValid = date instanceof Date && !Number.isNaN(date.getTime());
        if (!isValid) return;

        this.hasFormatError = false;

        const external = dateToExternal(date);
        const displayValue = this.formatDateForInput(date);

        this.$nextTick(() => {
          if (this.maskInputEl) {
            this.maskInputEl.value = displayValue;
            if (this.maskController) {
              this.maskController.update(displayValue);
            }
          }
        });

        this.$emit("input", external);
        this.$emit("change", external);

        this.$nextTick(() => {
          if (this.fv) {
            const fieldName = this.field && this.field.name ? this.field.name : this.name;
            if (fieldName) this.fv.revalidateField(fieldName);
          }
        });
      },
    },
  },

  watch: {
    formatOptions: {
      deep: true,
      handler() {
        this.$nextTick(() => this.attachMask(true));
      },
    },
    readonly() {
      this.$nextTick(() => this.attachMask(true));
    },
    disabled() {
      this.$nextTick(() => this.attachMask(true));
    },
  },

  mounted() {
    if (this.size === "sm") {
      this.style = { paddingTop: "2px", paddingBottom: "2px" };
    }
    this.attachMask(false);

    if (this.value) {
      this.$nextTick(() => {
        const dateObj = externalToDate(this.value);
        if (!dateObj) return;

        const displayValue = this.formatDateForInput(dateObj);

        if (this.maskInputEl) {
          this.maskInputEl.value = displayValue;
          if (this.maskController) {
            this.maskController.update(displayValue);
          }
        }
      });
    }
  },

  beforeDestroy() {
    this.detachMask();
  },

  methods: {
    formatDateForInput(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

      const sep = (this.formatOptions && this.formatOptions.separator) || ".";
      const yearIsNumeric = this.formatOptions && this.formatOptions.year === "numeric";

      const d = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = yearIsNumeric ? String(date.getFullYear()) : String(date.getFullYear()).slice(-2);

      return `${d}${sep}${m}${sep}${y}`;
    },

    disabledDate(date) {
      if (!date) return false;

      const t = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

      if (this.min instanceof Date && !Number.isNaN(this.min.getTime())) {
        const minT = new Date(this.min.getFullYear(), this.min.getMonth(), this.min.getDate()).getTime();
        if (t < minT) return true;
      }

      if (this.max instanceof Date && !Number.isNaN(this.max.getTime())) {
        const maxT = new Date(this.max.getFullYear(), this.max.getMonth(), this.max.getDate()).getTime();
        if (t > maxT) return true;
      }

      return false;
    },

    attachMask(force = false) {
      const pickerVm = this.$refs.picker;
      const pickerEl = pickerVm && pickerVm.$el;
      const input = pickerEl ? pickerEl.querySelector("input") : null;
      if (!input) return;

      if (!force && this.maskInputEl === input && this.maskController) return;

      this.detachMask();

      this.maskInputEl = input;

      this.maskController = createTextMaskInputElement({
        inputElement: this.maskInputEl,
        mask: buildTextMask(this.formatOptions),
        guide: true,
        placeholderChar: "_",
        keepCharPositions: true,
      });

      this.maskController.update(this.maskInputEl.value);

      this.maskInputHandler = (e) => {
        if (!this.maskController || !this.maskInputEl) return;

        this.maskController.update(this.maskInputEl.value);

        const v = this.maskInputEl.value || "";

        if (isMaskedValueComplete(v)) {
          const ok = isValidMaskedDateString(v, this.formatOptions, {
            min: this.min,
            max: this.max,
          });

          if (!ok) {
            this.hasFormatError = true;

            if (e && typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
            if (e && typeof e.stopPropagation === "function") e.stopPropagation();
            if (e && typeof e.preventDefault === "function") e.preventDefault();

            return;
          }

          this.hasFormatError = false;
        } else {
          this.hasFormatError = false;
        }
      };

      this.maskBlurHandler = (e) => this.onInternalBlur(e);

      this.maskInputEl.addEventListener("input", this.maskInputHandler, true);
      this.maskInputEl.addEventListener("blur", this.maskBlurHandler, true);
    },

    detachMask() {
      if (this.maskInputEl && this.maskInputHandler) {
        this.maskInputEl.removeEventListener("input", this.maskInputHandler, true);
      }
      if (this.maskInputEl && this.maskBlurHandler) {
        this.maskInputEl.removeEventListener("blur", this.maskBlurHandler, true);
      }

      this.maskController = null;
      this.maskInputEl = null;
      this.maskInputHandler = null;
      this.maskBlurHandler = null;
    },

    onInternalBlur(e) {
      if (!this.maskInputEl) return;

      const v = this.maskInputEl.value || "";

      if (isMaskedValueEmpty(v)) {
        this.hasFormatError = false;
        return;
      }

      if (!isMaskedValueComplete(v)) {
        this.maskInputEl.value = "";
        if (this.maskController) this.maskController.update("");
        this.hasFormatError = false;

        if (e && typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
        if (e && typeof e.stopPropagation === "function") e.stopPropagation();

        this.emitNullAndRevalidate();
        return;
      }

      const ok = isValidMaskedDateString(v, this.formatOptions, {
        min: this.min,
        max: this.max,
      });

      if (!ok) {
        this.hasFormatError = true;
        this.maskInputEl.value = "";
        if (this.maskController) this.maskController.update("");

        if (e && typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
        if (e && typeof e.stopPropagation === "function") e.stopPropagation();

        this.emitNullAndRevalidate();
        return;
      }

      this.hasFormatError = false;
    },

    emitNullAndRevalidate() {
      this.$emit("input", null);
      this.$emit("change", null);

      this.$nextTick(() => {
        if (this.fv) {
          const fieldName = this.field && this.field.name ? this.field.name : this.name;
          if (fieldName) this.fv.revalidateField(fieldName);
        }
      });
    },

    onChange() {
      if (this.fv) {
        const fieldName = this.field && this.field.name ? this.field.name : this.name;
        if (fieldName) this.fv.revalidateField(fieldName);
      }
      this.$emit("change");
    },

    onClear() {
      this.hasFormatError = false;

      if (this.maskInputEl) {
        this.maskInputEl.value = "";
      }
      if (this.maskController) {
        this.maskController.update("");
      }

      this.$emit("input", null);
      this.$emit("change", null);
      this.$emit("clear");

      this.$nextTick(() => {
        if (this.fv) {
          const fieldName = this.field && this.field.name ? this.field.name : this.name;
          if (fieldName) this.fv.revalidateField(fieldName);
        }
      });
    },

    onInputError() {
      this.hasFormatError = true;
      if (this.maskController) {
        this.maskController.update(this.maskInputEl ? this.maskInputEl.value : "");
      }
    },
  },
};
</script>

<style scoped>
.st-datepicker-error {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.2;
  color: #dc3545;
}

.st-has-error ::v-deep .mx-input {
  border-color: #dc3545 !important;
}

.st-has-error ::v-deep .mx-input:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

::v-deep .mx-input.is-invalid,
::v-deep .mx-input.is-invalid:hover,
::v-deep .mx-input.is-invalid:focus {
  border-color: #f64e60 !important;
}

::v-deep .st-datepicker-popup-right {
  left: auto !important;
  right: 0 !important;
  transform: none !important;
}
</style>